import ProductConfigurationContext from '@jetshop/core/components/ProductConfigurationProvider/ProductConfigurationContext';
import { useStockStatus } from '@jetshop/ui/Stock/StockStatusContainer';
import { PreOrderDateSelector } from '@jetshop/ui/PreOrderDateSelector';
import { theme } from '../Theming/Theming';
import React, { useContext } from 'react';
import { styled } from 'linaria/react';
import t from '@jetshop/intl';
import { GridPrice as Price } from '../Price';
import { Price as UIPrice } from '@jetshop/ui/Price';
import { useDynamicPrice } from '@jetshop/core/hooks/useDynamicPrice';
import NotifyWhenBack from './StockStatus/NotifyWhenBack';
import StockStatusIndicator from './StockStatus/StockStatusIndicator';
import { VariantSelector } from './VariantSelector/VariantSelector';
import InputWithLabel from '../Forms/Input';
import AddToCartForm from './AddToCart/AddToCartForm';
import { Form } from 'formik';
import AddToCartWrapper from './AddToCart/AddToCartWrapper';
import { ProductInfoMarginBottom } from './StyledComponents';
import { useProductValidationMessage } from './helpers';
import loadable from '@loadable/component';
import Spinner from '../ui/Spinner';
import ProductUspBox from './ProductUspBox';
import { Favourite } from '../ProductList/Favourite';
import { Campaigns } from './Campaigns';
import { Above } from '@jetshop/ui/Breakpoints';
import { BreadcrumbsWrapper } from '../Layout/BreadcrumbsWrapper';
import { Brand } from './Brand';
import WarehouseStock from './WarehouseStock/WarehouseStock';
import { getFontSize } from '../Theming/helpers';
import UpsellProducts from './UpsellProduct/UpsellProducts';
import { DropdownWrapper } from './VariantSelector/VariantDropdown';
import { PriceHistoryWrapper } from './Price/PriceHistory';
import { scrollTo } from '../ui/General';
import TextContent from '../ui/TextContent';
import SiblingProducts from './SiblingProducts/SiblingProducts';
import { KlarnaPayment } from './KlarnaPayment';

const LoadablePackageProduct = loadable(
  () => import('./PackageProduct/PackageProduct'),
  {
    fallback: <Spinner />,
  }
);

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  position: relative;
  ${theme.above.md} {
    align-items: flex-start;
  }
`;

export const Header = styled('header')`
  width: 100%;
  ${ProductInfoMarginBottom};
  .breadcrumbs-wrapper {
    padding: 0.5rem 0;
  }
`;

export const ProductHeading = styled('h1')`
  display: flex;
  justify-content: space-between;
  font-weight: ${theme.productGrid.product.name.fontWeight} !important;
  ${getFontSize(theme.typography.header.fontSize[2])}
`;

const ReadMoreButton = styled.button`
  margin-top: 0.5rem;
  ${ProductInfoMarginBottom}
  color: ${theme.colors.buy};
`;

const FlexRow = styled('div')`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
`;

export const PriceWrapper = styled('div')`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  [data-flight-price] {
    margin-right: 10px;
    ${UIPrice.Normal} {
      ${getFontSize(theme.productPage.price.normal.fontSize)}
    }
    ${UIPrice.New} {
      ${getFontSize(theme.productPage.price.new.fontSize)}
      margin-bottom: 0 !important;
    }
    ${UIPrice.Old} {
      ${getFontSize(theme.productPage.price.old.fontSize)}
    }
  }
`;

const Col = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  &:last-child {
    align-items: flex-end;
    flex-grow: 1;
  }
`;

const StockStatusWrapper = styled('div')`
  .stock-wrapper {
    margin-bottom: 0;
  }
  .stock-label {
    font-weight: ${theme.weights.normal};
    text-transform: none;
  }
  .free-freight-stock {
    display: inline-block;
    margin: 0 0 0 0.8rem;
  }
`;

const VariantsWrapper = styled('div')`
  width: 100%;
  > div {
    ${ProductInfoMarginBottom}
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  width: 100%;
  flex-direction: column;
`;

const ShortDescriptionWrapper = styled('article')`
  line-height: ${theme.typography.paragraph.lineHeight};
  ${theme.below.md} {
    font-size: 0.9rem;
  }
  p {
    margin: 0 0 0.5rem 0;
  }
`;

const PreOrderWrapper = styled(DropdownWrapper)`
  && {
    [data-flight-dropdown-items] {
      width: 100%;
      height: 200px;
      overflow: scroll;
    }
  }
`;

export const ShortDescription = ({ shortDescription }) => {
  const shortdescr = shortDescription.replace(/\n/g, '<br />');
  return (
    <ShortDescriptionWrapper>
      <TextContent content={shortdescr} />
    </ShortDescriptionWrapper>
  );
};

const Details = ({ product, variantHandler, breadcrumbProps }) => {
  const {
    selectedVariant: selectedVariation,
    validation: variantValidation,
    getMissingOptions,
    missingOptions,
  } = variantHandler;

  const selected = selectedVariation || product;
  const configurationContext = useContext(ProductConfigurationContext);
  const stockStatus = useStockStatus(selectedVariation || product);
  const price = useDynamicPrice(product, selectedVariation);
  const outOfStockNotify = stockStatus.status === 'notifyWhenBack';

  const { hasVariants, hasUpsell, hidePrice } = product;
  const productValidation = useProductValidationMessage({
    missingOptions,
    variantValidation,
    hasVariants,
    stockStatus,
  });

  const completeProduct = product.stockStatus?.text;

  return (
    <Wrapper>
      <Header>
        <div style={{ minHeight: '3.3rem', width: '100%' }}>
          <BreadcrumbsWrapper {...breadcrumbProps} />
        </div>
        <Brand product={product} />
        <ProductHeading data-testid="product-title">
          {product.name}
          <Favourite product={product} variant={selectedVariation} />
        </ProductHeading>
      </Header>
      <Above breakpoint="lg">
        {(matches) =>
          matches && product?.shortDescription ? (
            <>
              <ShortDescription shortDescription={product.shortDescription} />
              <ReadMoreButton onClick={() => scrollTo('product-tabs-wrapper')}>
                {t('Read more')}
              </ReadMoreButton>
            </>
          ) : (
            <></>
          )
        }
      </Above>
      <SiblingProducts product={product} />
      {product?.isPackage ? (
        <LoadablePackageProduct product={product} />
      ) : (
        <AddToCartForm
          product={product}
          variant={selectedVariation}
          getMissingOptions={getMissingOptions}
        >
          {(addToCartTrigger) => {
            return (
              <>
                {product.hasVariants && (
                  <VariantsWrapper>
                    <VariantSelector
                      product={product}
                      variantHandler={variantHandler}
                      showValidation={!!productValidation.validationMessage}
                    />
                  </VariantsWrapper>
                )}
                <PreOrderWrapper>
                  <PreOrderDateSelector product={product} />
                </PreOrderWrapper>
                <FlexRow>
                  <Col>
                    <PriceWrapper>
                      <Price
                        {...price}
                        hidePrice={hidePrice}
                        css={{
                          opacity:
                            configurationContext && configurationContext.loading
                              ? 0.3
                              : 1,
                        }}
                      />
                      {!hidePrice && (
                        <PriceHistoryWrapper
                          articleNumber={product.articleNumber}
                          variant={selectedVariation}
                        />
                      )}
                    </PriceWrapper>
                  </Col>
                  <Col>
                    {completeProduct && (
                      <StockStatusWrapper>
                        <StockStatusIndicator
                          status={stockStatus.status}
                          text={stockStatus.text}
                        />
                      </StockStatusWrapper>
                    )}
                  </Col>
                </FlexRow>

                {hasUpsell && (
                  <UpsellProducts
                    product={product}
                    addToCartTrigger={addToCartTrigger}
                  />
                )}
                <StyledForm>
                  {product.customerComments &&
                    product.customerComments.map((comment) => (
                      <InputWithLabel
                        wrapperClassName="customer-comment-input-wrapper"
                        className="customer-comment-input"
                        data-testid={`${comment.name}-input`}
                        id={`comments['${comment.name}']`}
                        name={`comments['${comment.name}']`}
                        label={comment.name}
                        key={comment.name}
                      />
                    ))}
                  {!outOfStockNotify && completeProduct && !hidePrice && (
                    <AddToCartWrapper
                      variantValidation={variantValidation}
                      productValidation={productValidation}
                      product={product}
                    />
                  )}
                </StyledForm>
                {outOfStockNotify && (
                  <NotifyWhenBack articleNumber={selected.articleNumber} />
                )}
                {(product?.warehouseStock?.length > 0 ||
                  product?.variants?.values[0]?.warehouseStock?.length > 0) && (
                  <WarehouseStock
                    product={product}
                    variantHandler={variantHandler}
                  />
                )}
              </>
            );
          }}
        </AddToCartForm>
      )}
      <KlarnaPayment price={price?.price?.incVat} />
      <ProductUspBox />
      <Campaigns campaigns={product.campaigns} />
    </Wrapper>
  );
};

export default Details;
